import { Injectable } from '@angular/core';
import { DefaultWidget } from '@app/shared/models/default-widget.model';

@Injectable({ providedIn: 'root' })
export class DefaultWidgetsService {

  private _arrayWidget: DefaultWidget = {
    name: 'array',
    defaultJson: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          textContent: {
            type: 'string',
            description: 'text',
            widget: 'string'
          },
          numberContent: {
            type: 'number',
            description: 'number',
            widget: 'number'
          }
        }
      }
    }
  };

  private _numberWidget: DefaultWidget = {
    name: 'number',
    defaultJson: {
      type: 'number',
      description: 'number',
      widget: 'number'
    }
  };

  private _checkboxWidget: DefaultWidget = {
    name: 'checkbox',
    defaultJson: {
      type: 'boolean',
      description: 'Checkbox',
      widget: 'checkbox'
    }
  };

  private _radioWidget: DefaultWidget = {
    name: 'radio',
    defaultJson: {
      type: 'string',
      oneOf: [
        {
          description: 'Warn',
          enum: [
            'Warn'
          ]
        },
        {
          description: 'Fail',
          enum: [
            'Fail'
          ]
        }
      ],
      widget: 'radio'
    }
  };

  private _selectWidget: DefaultWidget = {
    name: 'select',
    defaultJson: {
      type: 'string',
      widget: 'select',
      description: 'Component',
      oneOf: [
        {
          enum: ['Option 1'],
          description: 'Braking'
        },
        {
          enum: ['Option 2'],
          description: 'Option 2'
        },
        {
          enum: ['Option 3'],
          description: 'Option 3'
        }
      ]
    }
  };

  private _stopWatchWidget: DefaultWidget = {
    name: 'stopwatch',
    defaultJson: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          startMs: {
            type: 'number'
          },
          endMs: {
            type: 'number'
          }
        },
        required: [
          'startMs',
          'endMs'
        ]
      },
      widget: {
        id: 'stopwatch',
        laps: 0,
        lapTextTemplate: 'Comp ${lap} Recovery Time',
        reverseLapIndex: false,
        hideMs: true,
        startButtonLabel: '5 application mark timing',
        stopButtonLabel: 'Stop full Air Pressure'
      },
      minItems: 1
    }
  };

  private _textAreaWidget: DefaultWidget = {
    name: 'textarea',
    defaultJson: {
      type: 'string',
      description: 'textarea',
      widget: 'textarea'
    }
  };

  private _textWidget: DefaultWidget = {
    name: 'text',
    defaultJson: {
      type: 'string',
      description: 'Text Widget',
      widget: {
        id: 'string'
      }
    }
  };

  private _diagramWidget: DefaultWidget = {
    name: 'diagram',
    defaultJson: {}
  };

  private _buttonWidget: DefaultWidget = {
    name: 'button',
    defaultJson: {
      buttons: [{
        id: 'button-submit',
        label: 'Add Inspection',
        class: 'home-detail'
      }]
    }
  };

  private _submitbuttonWidget: DefaultWidget = {
    name: 'submit',
    defaultJson: {
      buttons: [{
        id: 'button-submit',
        label: 'Add Inspection',
        widget: 'submit',
        class: 'home-detail'
      }]
    }
  };

  private _eventButtonWidget: DefaultWidget = {
    name: 'event-button',
    defaultJson: {
      buttons: [{
        id: 'button-event',
        label: 'Print',
        widget: 'event-button',
        class: 'col-5',
        config: {
          color: 'primary',
          eventEndpoint: 'https://prod-20.australiasoutheast.logic.azure.com:443/workflows/e8014107285d4a519b47ab6cfa9f88bc/triggers/request/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Frequest%2Frun&sv=1.0&sig=xIkBcxsDHJw40RQKLtdoBeGC-GDWFQS5xlV74up3wE0',
          requestMethod: 'POST'
        },
        ableIf: {
          printerId: [
            '$ANY$'
          ]
        }
      }]
    }
  };

  getArrayWidget(): DefaultWidget {
    return this.transformObject(this._arrayWidget);
  }

  getCheckBoxWidget(): DefaultWidget {
    return this.transformObject(this._checkboxWidget);
  }

  getNumberWidget(): DefaultWidget {
    return this.transformObject(this._numberWidget);
  }

  getRadioWidget(): DefaultWidget {
    return this.transformObject(this._radioWidget);
  }

  getSelectWidget() {
    return this.transformObject(this._selectWidget);
  }

  getStopwatchWidget(): DefaultWidget {
    return this.transformObject(this._stopWatchWidget);
  }

  getTextareaWidget(): DefaultWidget {
    return this.transformObject(this._textAreaWidget);
  }

  getTextWidget(): DefaultWidget {
    return this.transformObject(this._textWidget);
  }

  getDiagramWidget(): DefaultWidget {
    return this.transformObject(this._diagramWidget);
  }

  getButtonWidget(): DefaultWidget {
    return this._buttonWidget;
  }

  getSubmitButtonWidget(): DefaultWidget {
    return this._submitbuttonWidget;
  }

  getEventButtonWidget(): DefaultWidget {
    return this._eventButtonWidget;
  }

  private randomName(object: DefaultWidget): string {
    return Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substr(0, 5) + object.name;
  }

  private transformObject(object: DefaultWidget): DefaultWidget {
    const transformedObject = Object.assign({}, object);
    transformedObject.name = this.randomName(object);
    return transformedObject;
  }

  getWidgetByName(name: string) {
    switch (name) {
      case 'array':
        return this.getArrayWidget();
      case 'checkbox':
        return this.getCheckBoxWidget();
      case 'number':
        return this.getNumberWidget();
      case 'radio':
        return this.getRadioWidget();
      case 'select':
        return this.getSelectWidget();
      case 'stopwatch':
        return this.getStopwatchWidget();
      case 'textarea':
        return this.getTextareaWidget();
      case 'text':
        return this.getTextWidget();
      case 'diagram':
        return this.getDiagramWidget();
      case 'button':
        return this.getButtonWidget();
      case 'submit':
        return this.getSubmitButtonWidget();
      case 'event-button':
        return this.getEventButtonWidget();
      default:
        return null;
    }
  }

  getWidgetList(): DefaultWidget[] {
    return [
      this._arrayWidget,
      this._checkboxWidget,
      this._numberWidget,
      this._radioWidget,
      this._selectWidget,
      this._stopWatchWidget,
      this._textAreaWidget,
      this._textWidget,
      // Todo: Uncomment when the widgets is ready
      // this._diagramWidget,
      // this._buttonWidget,
      // this._submitbuttonWidget,
      // this._eventButtonWidget
    ];
  }

}
